import "./generated-background-area.css";

import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import { Slider, TextField } from "@mui/material";
import React, { ReactElement } from "react";

import { theme } from "../../styles/mui-theme";
import { classNames } from "../../utils/classes";

import { getAIBackgroundDataUrl, getAIBackgroundStyle } from "./ai-bg-texture";

const DRAG_PADDING_PX = 24;
const MIN_ZOOM = 50;
const MAX_ZOOM = 300;
const WHEEL_ZOOM_STEP = 5;

export type GeneratedBackgroundAreaProps = {
  generatedBackground?: string;
  generatedBackgroundName: string;
  isGenerating?: boolean;
  onGeneratedBackgroundNameChange: (name: string) => void;
};

export function GeneratedBackgroundArea({
  generatedBackground,
  generatedBackgroundName,
  isGenerating = false,
  onGeneratedBackgroundNameChange,
}: GeneratedBackgroundAreaProps): ReactElement {
  const [zoom, setZoom] = React.useState(100);
  const [offsetX, setOffsetX] = React.useState(0);
  const [offsetY, setOffsetY] = React.useState(0);
  const [isDragging, setIsDragging] = React.useState(false);
  const imageContainerRef = React.useRef<HTMLDivElement>(null);
  const imageRef = React.useRef<HTMLImageElement>(null);
  const dragStateRef = React.useRef<{
    pointerId: number;
    startClientX: number;
    startClientY: number;
    startOffsetX: number;
    startOffsetY: number;
  } | null>(null);
  const backgroundStyle = getAIBackgroundStyle("#F0F0F0");
  const animatedIconStyle = React.useMemo(
    () => ({
      backgroundImage: getAIBackgroundDataUrl(theme.palette.ai.main),
    }),
    [],
  );

  const clampOffsets = React.useCallback(
    (nextX: number, nextY: number): { x: number; y: number } => {
      const container = imageContainerRef.current;
      const image = imageRef.current;
      if (!container || !image) {
        return { x: nextX, y: nextY };
      }

      const containerRect = container.getBoundingClientRect();
      const imageRect = image.getBoundingClientRect();
      const maxOffsetX = Math.max(
        DRAG_PADDING_PX,
        Math.abs(imageRect.width - containerRect.width) / 2 + DRAG_PADDING_PX,
      );
      const maxOffsetY = Math.max(
        DRAG_PADDING_PX,
        Math.abs(imageRect.height - containerRect.height) / 2 + DRAG_PADDING_PX,
      );

      return {
        x: Math.min(maxOffsetX, Math.max(-maxOffsetX, nextX)),
        y: Math.min(maxOffsetY, Math.max(-maxOffsetY, nextY)),
      };
    },
    [],
  );

  function handleImagePointerDown(
    event: React.PointerEvent<HTMLDivElement>,
  ): void {
    event.preventDefault();
    dragStateRef.current = {
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startOffsetX: offsetX,
      startOffsetY: offsetY,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    setIsDragging(true);
  }

  function handleImagePointerMove(
    event: React.PointerEvent<HTMLDivElement>,
  ): void {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) {
      return;
    }

    const deltaX = event.clientX - dragState.startClientX;
    const deltaY = event.clientY - dragState.startClientY;
    const clamped = clampOffsets(
      dragState.startOffsetX + deltaX,
      dragState.startOffsetY + deltaY,
    );
    setOffsetX(clamped.x);
    setOffsetY(clamped.y);
  }

  function handleImagePointerUp(
    event: React.PointerEvent<HTMLDivElement>,
  ): void {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) {
      return;
    }

    dragStateRef.current = null;
    event.currentTarget.releasePointerCapture(event.pointerId);
    setIsDragging(false);
  }

  const handleImageWheel = React.useCallback(
    (event: WheelEvent): void => {
      event.preventDefault();

      const container = imageContainerRef.current;
      if (!container) {
        return;
      }

      const zoomDelta = event.deltaY < 0 ? WHEEL_ZOOM_STEP : -WHEEL_ZOOM_STEP;
      const nextZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom + zoomDelta));
      if (nextZoom === zoom) {
        return;
      }

      const containerRect = container.getBoundingClientRect();
      const centerX = containerRect.left + containerRect.width / 2;
      const centerY = containerRect.top + containerRect.height / 2;
      const cursorX = event.clientX - centerX;
      const cursorY = event.clientY - centerY;
      const oldScale = zoom / 100;
      const newScale = nextZoom / 100;

      const nextOffsetX = cursorX - ((cursorX - offsetX) / oldScale) * newScale;
      const nextOffsetY = cursorY - ((cursorY - offsetY) / oldScale) * newScale;
      const clamped = clampOffsets(nextOffsetX, nextOffsetY);

      setZoom(nextZoom);
      setOffsetX(clamped.x);
      setOffsetY(clamped.y);
    },
    [clampOffsets, offsetX, offsetY, zoom],
  );

  React.useEffect(() => {
    const clamped = clampOffsets(offsetX, offsetY);
    if (clamped.x !== offsetX) {
      setOffsetX(clamped.x);
    }
    if (clamped.y !== offsetY) {
      setOffsetY(clamped.y);
    }
  }, [clampOffsets, offsetX, offsetY]);

  React.useEffect(() => {
    if (!generatedBackground) {
      setZoom(100);
      setOffsetX(0);
      setOffsetY(0);
      setIsDragging(false);
      dragStateRef.current = null;
    }
  }, [generatedBackground]);

  React.useEffect(() => {
    const container = imageContainerRef.current;
    if (!container) {
      return;
    }

    container.addEventListener("wheel", handleImageWheel, { passive: false });

    return () => {
      container.removeEventListener("wheel", handleImageWheel);
    };
  }, [handleImageWheel]);

  return (
    <div className="flex-grow min-h-0 relative z-10">
      <div
        className="w-full h-full overflow-hidden relative"
        style={{
          ...backgroundStyle,
        }}
      >
        {isGenerating && (
          <div
            className="ai-background-wave-overlay"
            style={animatedIconStyle}
          />
        )}
        {generatedBackground && (
          <>
            <div
              ref={imageContainerRef}
              className="h-full relative flex justify-center overflow-hidden"
              onPointerDown={handleImagePointerDown}
              onPointerMove={handleImagePointerMove}
              onPointerUp={handleImagePointerUp}
              onPointerCancel={handleImagePointerUp}
              style={{
                cursor: isDragging ? "grabbing" : "grab",
                touchAction: "none",
                userSelect: "none",
              }}
            >
              <img
                ref={imageRef}
                src={generatedBackground}
                alt="Generated Background"
                style={{
                  height: "100%",
                  aspectRatio: "1/1",
                  objectPosition: "50% top",
                  transform: `translate(${offsetX}px, ${offsetY}px) scale(${zoom / 100})`,
                  transformOrigin: "center center",
                  transition: isDragging ? "none" : "transform 120ms ease-out",
                  pointerEvents: "none",
                }}
                className={classNames("object-contain")}
              />
              <div
                className="absolute bottom-0 w-full p-4 flex flex-wrap justify-between items-stretch gap-2"
                onClick={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
              >
                <TextField
                  onChange={(e) =>
                    onGeneratedBackgroundNameChange(e.target.value)
                  }
                  type="primary"
                  className="bg-white rounded-[15px] min-w-[220px]"
                  placeholder="Background Name"
                  value={generatedBackgroundName}
                  InputProps={{
                    endAdornment: <EditIcon />,
                    style: { borderRadius: "15px" },
                  }}
                />
                <div className="bg-white rounded-[15px] shadow px-3 py-2 min-w-[220px] max-w-full flex items-center gap-2">
                  <SearchIcon />
                  <Slider
                    className="flex-1"
                    size="small"
                    value={zoom}
                    min={MIN_ZOOM}
                    max={MAX_ZOOM}
                    onChange={(_, value) => setZoom(value as number)}
                  />
                  <div className="text-sm w-[60px] h-[30px] px-2 rounded-md border border-[#CECFD2] flex items-center justify-center">
                    {zoom}%
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
